import { useNavigate } from "react-router-dom";

import {
    MapPinIcon,
    CalendarIcon,
    UserIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/outline";

import { useState, useEffect } from "react";
import axios from "axios";

export default function SearchBar() {
    const [form, setForm] = useState({
        destination: "",
        checkIn: "",
        checkOut: "",
        guests: 1,
    });

    const navigate = useNavigate();

    const [destinations, setDestinations] = useState([]);

    // =========================================================
    // POPULAR SEARCHES
    // =========================================================

    const popularSearches = [
        "Dubai",
        "Abu Dhabi",
        "Sharjah",
        "Ajman",
        "Ras Al Khaimah",
    ];

    // =========================================================
    // HANDLE FORM CHANGE
    // =========================================================

    const handleChange = (e) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    // =========================================================
    // HANDLE SUBMIT
    // =========================================================

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.destination.trim()) {
            alert("Please enter the destination");
            return;
        }

        navigate("/search-hotels", {
            state: form,
        });
    };

    // =========================================================
    // POPULAR SEARCH CLICK
    // =========================================================

    const handlePopularSearch = (destination) => {
        const updatedForm = {
            ...form,
            destination,
        };

        setForm(updatedForm);

        navigate("/search-hotels", {
            state: updatedForm,
        });
    };

    // =========================================================
    // LOAD DESTINATIONS
    // =========================================================

    useEffect(() => {
        axios
            .get("https://marinadubai.com/api/destinations")
            .then((res) => {
                setDestinations(res.data);
            })
            .catch((err) => console.log(err));
    }, []);

    // =========================================================
    // FORMAT GUEST TEXT
    // =========================================================

    const guestText =
        Number(form.guests) === 1
            ? "1 Guest"
            : `${form.guests} Guests`;

    // =========================================================
    // MAIN UI
    // =========================================================

    return (
        <div className="w-full">

            {/* =====================================================
                SEARCH CARD
            ===================================================== */}



            {/* =================================================
                    SEARCH FORM
                ================================================= */}

            <form
                onSubmit={handleSubmit}
                className="
                        flex
                        flex-col

                        lg:flex-row
                        lg:items-stretch
                    "
            >

                {/* =================================================
                        DESTINATION
                    ================================================= */}

                <div
                    className="
                            group
                            relative
                            min-w-0
                            flex-1

                            rounded-[12px]
                            px-4
                            py-3

                            transition-all
                            duration-200

                            hover:bg-[#FAF8F3]
                            focus-within:bg-[#FAF8F3]

                            lg:rounded-none
                            lg:border-r
                            lg:border-[#E9E5DC]
                            lg:px-5
                            lg:py-3
                        "
                >
                    <div
                        className="
                                flex
                                items-center
                                gap-2
                                mb-1.5
                            "
                    >
                        <MapPinIcon
                            className="
                                    h-[19px]
                                    w-[19px]
                                    shrink-0
                                    text-[#0B5552]
                                "
                            strokeWidth={1.7}
                        />

                        <span
                            className="
                                    text-[10px]
                                    font-medium
                                    text-[#6F7D7C]
                                "
                        >
                            Destination
                        </span>
                    </div>

                    <div className="relative">
                        <select
                            name="destination"
                            value={form.destination}
                            onChange={handleChange}
                            className="
                                    w-full
                                    appearance-none
                                    bg-transparent
                                    pr-7
                                    text-[13px]
                                    font-medium
                                    text-[#183F40]
                                    outline-none
                                    cursor-pointer

                                    placeholder:text-[#8C9897]
                                "
                        >
                            <option value="">
                                Where are you going?
                            </option>

                            {destinations.map((dest) => (
                                <option
                                    key={dest.id}
                                    value={dest.name}
                                >
                                    {dest.name}
                                </option>
                            ))}
                        </select>

                        <ChevronDownIcon
                            className="
                                    pointer-events-none
                                    absolute
                                    right-0
                                    top-1/2
                                    h-4
                                    w-4
                                    -translate-y-1/2
                                    text-[#174F4D]
                                "
                            strokeWidth={1.7}
                        />
                    </div>
                </div>

                {/* =================================================
                        CHECK IN
                    ================================================= */}

                <div
                    className="
                            group
                            relative
                            min-w-0
                            flex-1

                            rounded-[12px]
                            px-4
                            py-3

                            border-t
                            border-[#EEEAE1]

                            transition-all
                            duration-200

                            hover:bg-[#FAF8F3]
                            focus-within:bg-[#FAF8F3]

                            lg:rounded-none
                            lg:border-t-0
                            lg:border-r
                            lg:border-[#E9E5DC]
                            lg:px-5
                            lg:py-3
                        "
                >
                    <div
                        className="
                                flex
                                items-center
                                gap-2
                                mb-1.5
                            "
                    >
                        <CalendarIcon
                            className="
                                    h-[19px]
                                    w-[19px]
                                    shrink-0
                                    text-[#0B5552]
                                "
                            strokeWidth={1.7}
                        />

                        <span
                            className="
                                    text-[10px]
                                    font-medium
                                    text-[#6F7D7C]
                                "
                        >
                            Check In
                        </span>
                    </div>

                    <input
                        type="date"
                        name="checkIn"
                        value={form.checkIn}
                        onChange={handleChange}
                        className="
                                w-full
                                bg-transparent
                                text-[13px]
                                font-medium
                                text-[#183F40]
                                outline-none
                                cursor-pointer

                                [&::-webkit-calendar-picker-indicator]:opacity-0
                                [&::-webkit-calendar-picker-indicator]:absolute
                                [&::-webkit-calendar-picker-indicator]:right-0
                                [&::-webkit-calendar-picker-indicator]:w-full
                                [&::-webkit-calendar-picker-indicator]:h-full
                                [&::-webkit-calendar-picker-indicator]:cursor-pointer
                            "
                    />


                </div>

                {/* =================================================
                        CHECK OUT
                    ================================================= */}

                <div
                    className="
                            group
                            relative
                            min-w-0
                            flex-1

                            rounded-[12px]
                            px-4
                            py-3

                            border-t
                            border-[#EEEAE1]

                            transition-all
                            duration-200

                            hover:bg-[#FAF8F3]
                            focus-within:bg-[#FAF8F3]

                            lg:rounded-none
                            lg:border-t-0
                            lg:border-r
                            lg:border-[#E9E5DC]
                            lg:px-5
                            lg:py-3
                        "
                >
                    <div
                        className="
                                flex
                                items-center
                                gap-2
                                mb-1.5
                            "
                    >
                        <CalendarIcon
                            className="
                                    h-[19px]
                                    w-[19px]
                                    shrink-0
                                    text-[#0B5552]
                                "
                            strokeWidth={1.7}
                        />

                        <span
                            className="
                                    text-[10px]
                                    font-medium
                                    text-[#6F7D7C]
                                "
                        >
                            Check Out
                        </span>
                    </div>

                    <input
                        type="date"
                        name="checkOut"
                        value={form.checkOut}
                        onChange={handleChange}
                        className="
                                w-full
                                bg-transparent
                                text-[13px]
                                font-medium
                                text-[#183F40]
                                outline-none
                                cursor-pointer

                                [&::-webkit-calendar-picker-indicator]:opacity-0
                                [&::-webkit-calendar-picker-indicator]:absolute
                                [&::-webkit-calendar-picker-indicator]:right-0
                                [&::-webkit-calendar-picker-indicator]:w-full
                                [&::-webkit-calendar-picker-indicator]:h-full
                                [&::-webkit-calendar-picker-indicator]:cursor-pointer
                            "
                    />


                </div>

                {/* =================================================
                        GUESTS
                    ================================================= */}

                <div
                    className="
                            group
                            relative
                            min-w-0
                            flex-1

                            rounded-[12px]
                            px-4
                            py-3

                            border-t
                            border-[#EEEAE1]

                            transition-all
                            duration-200

                            hover:bg-[#FAF8F3]
                            focus-within:bg-[#FAF8F3]

                            lg:rounded-none
                            lg:border-t-0
                            lg:px-5
                            lg:py-3
                        "
                >
                    <div
                        className="
                                flex
                                items-center
                                gap-2
                                mb-1.5
                            "
                    >
                        <UserIcon
                            className="
                                    h-[19px]
                                    w-[19px]
                                    shrink-0
                                    text-[#0B5552]
                                "
                            strokeWidth={1.7}
                        />

                        <span
                            className="
                                    text-[10px]
                                    font-medium
                                    text-[#6F7D7C]
                                "
                        >
                            Guests
                        </span>
                    </div>

                    <div className="relative">
                        <select
                            name="guests"
                            value={form.guests}
                            onChange={handleChange}
                            className="
                                    w-full
                                    appearance-none
                                    bg-transparent
                                    pr-7
                                    text-[13px]
                                    font-medium
                                    text-[#183F40]
                                    outline-none
                                    cursor-pointer
                                "
                        >
                            {[1, 2, 3, 4, 5, 6].map(
                                (num) => (
                                    <option
                                        key={num}
                                        value={num}
                                    >
                                        {num}{" "}
                                        {num === 1
                                            ? "Guest"
                                            : "Guests"}
                                    </option>
                                )
                            )}
                        </select>

                        <ChevronDownIcon
                            className="
                                    pointer-events-none
                                    absolute
                                    right-0
                                    top-1/2
                                    h-4
                                    w-4
                                    -translate-y-1/2
                                    text-[#174F4D]
                                "
                            strokeWidth={1.7}
                        />
                    </div>
                </div>

                {/* =================================================
                        SEARCH BUTTON
                    ================================================= */}

                <div
                    className="
                            mt-2
                            w-full

                            lg:mt-0
                            lg:w-auto
                            lg:min-w-[168px]
                        "
                >
                    <button
                        type="submit"
                        className="
                                flex
                                h-full
                                min-h-[56px]
                                w-full
                                items-center
                                justify-center
                                gap-2.5

                                rounded-[11px]

                                bg-[#0B5552]

                                px-7

                                text-[13px]
                                font-semibold
                                text-white

                                shadow-[0_5px_15px_rgba(11,85,82,0.18)]

                                transition-all
                                duration-200

                                hover:bg-[#073F3D]
                                hover:shadow-[0_8px_20px_rgba(11,85,82,0.24)]

                                active:scale-[0.98]

                                whitespace-nowrap
                            "
                    >
                        <MagnifyingGlassIcon
                            className="
                                    h-[19px]
                                    w-[19px]
                                "
                            strokeWidth={1.8}
                        />

                        <span>
                            Search Hotels
                        </span>
                    </button>
                </div>
            </form>
        </div>
    );
}